import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import confetti from "canvas-confetti";
import { toPng } from "html-to-image";
import { Share2, RotateCcw, Trophy, Download, Coins, Star, Zap, Clover, Gift } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { api, RANKS } from "../lib/api";
import { CareerCard } from "../components/CareerCard";
import { sfx } from "../lib/sound";
import { useAuth } from "../auth/AuthContext";
import { SKIN_STYLES } from "../lib/skins";
import { store } from "../lib/storage";

const useCountUp = (target, duration = 1400) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const p = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return val;
};

export const ResultScreen = ({ result, domainLabel, onReplay, onBonusReplay, onLeaderboard, endReason, challenge }) => {
  const { t, dict } = useLang();
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const rankKey = result.rank || "anonyme";
  const rc = RANKS[rankKey];
  const score = useCountUp(result.score || 0);
  const { user } = useAuth();
  const owned = (user && user.owned_skins) || [];
  const [skin, setSkin] = useState(() => store.get("de_skin") || "");
  useEffect(() => {
    store.set("de_skin", skin);
  }, [skin]);
  const activeSkin = skin && owned.includes(skin) ? skin : "";

  useEffect(() => {
    if (rankKey === "legende" || rankKey === "star") {
      sfx.win();
      const end = Date.now() + 1200;
      const colors = [rc.c1, rc.c2, "#ffffff"];
      (function frame() {
        confetti({ particleCount: 4, angle: 60, spread: 70, origin: { x: 0 }, colors });
        confetti({ particleCount: 4, angle: 120, spread: 70, origin: { x: 1 }, colors });
        if (Date.now() < end) requestAnimationFrame(frame);
      })();
    } else {
      sfx.lose();
    }
  }, [rankKey, rc]);

  const endMsg =
    endReason === "temps" ? t("endTime") : endReason === "energie" ? t("endEnergy") : t("endFinish");

  const capture = async () => {
    if (!cardRef.current) return null;
    return await toPng(cardRef.current, { width: 1080, height: 1920, pixelRatio: 1, cacheBust: true });
  };

  const handleShare = async () => {
    setBusy(true);
    const shareUrl = result.id ? api.challengeUrl(result.id) : (typeof window !== "undefined" ? window.location.origin : "");
    const shareText = `${t("shareChallenge", { score: (result.score || 0).toLocaleString(), rank: dict.ranks[rankKey] })} ${shareUrl}`.trim();
    try {
      const dataUrl = await capture();
      if (result.id) api.trackShare(result.id, navigator.share ? "native" : "clipboard").catch(() => {});
      if (dataUrl && navigator.canShare) {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], "destin-express.png", { type: "image/png" });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], text: shareText, title: "Destin Express" });
          setBusy(false);
          return;
        }
      }
      if (navigator.share) {
        await navigator.share({ text: shareText, title: "Destin Express" });
      } else {
        await navigator.clipboard.writeText(shareText);
        alert(t("shareCopied"));
      }
    } catch (e) {
      try {
        await navigator.clipboard.writeText(shareText);
        alert(t("shareCopied"));
      } catch (_) {}
    }
    setBusy(false);
  };

  const handleDownload = async () => {
    setBusy(true);
    try {
      const dataUrl = await capture();
      if (dataUrl) {
        const a = document.createElement("a");
        a.href = dataUrl;
        a.download = `destin-express-${rankKey}.png`;
        a.click();
      }
    } catch (_) {}
    setBusy(false);
  };

  const stats = [
    { icon: Coins, label: dict.argent, value: `${(result.argent || 0).toLocaleString()}€`, color: "#fbbf24" },
    { icon: Star, label: dict.gloire, value: result.gloire || 0, color: "#f093fb" },
    { icon: Zap, label: dict.energie, value: result.energie || 0, color: "#38ef7d" },
    { icon: Clover, label: dict.chance, value: result.chance || 0, color: "#06b6d4" },
  ];

  return (
    <div className="flex min-h-full flex-col px-5 py-6" data-testid="result-screen">
      <p className="text-center text-sm text-white/40">{endMsg}</p>

      {challenge && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="challenge-result-banner"
          className={`mx-auto mt-3 max-w-sm rounded-2xl border px-4 py-3 text-center text-sm font-semibold ${
            (result.score || 0) >= (challenge.score || 0)
              ? "border-[#38ef7d]/40 bg-[#38ef7d]/10 text-[#38ef7d]"
              : "border-[#f5576c]/40 bg-[#f5576c]/10 text-[#f5576c]"
          }`}
        >
          {(result.score || 0) >= (challenge.score || 0)
            ? t("challengeBeaten", {
                pseudo: challenge.pseudo,
                n: ((result.score || 0) - (challenge.score || 0)).toLocaleString(),
              })
            : t("challengeMissed", {
                pseudo: challenge.pseudo,
                n: ((challenge.score || 0) - (result.score || 0)).toLocaleString(),
              })}
        </motion.div>
      )}

      <div className="flex flex-1 flex-col items-center justify-center py-4">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="font-display text-sm uppercase tracking-[0.3em] text-white/50"
        >
          🏆 {t("yourDestiny")}
        </motion.p>

        <motion.h1
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ type: "spring", stiffness: 200, damping: 12 }}
          data-testid="result-rank"
          className="mt-2 font-display text-6xl font-black uppercase tracking-tighter"
          style={{
            background: `linear-gradient(90deg, ${rc.c1}, ${rc.c2})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            filter: `drop-shadow(0 0 24px ${rc.glow})`,
          }}
        >
          {dict.ranks[rankKey]}
        </motion.h1>
        <p className="mt-1 text-lg italic text-white/70">{dict.rankTitles[rankKey]}</p>

        <motion.div
          data-testid="result-score"
          className="mt-4 font-display text-7xl font-black tracking-tighter"
          style={{
            background: `linear-gradient(90deg, ${rc.c1}, ${rc.c2})`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
          }}
        >
          {score.toLocaleString()}
        </motion.div>
        <p className="text-xs uppercase tracking-[0.3em] text-white/40">{t("points")}</p>

        {result.rankPosition && (
          <div className="mt-4 flex items-center gap-4 rounded-full border border-white/10 bg-white/5 px-5 py-2 text-sm">
            <span className="text-white/60">
              {t("worldRank")}: <span className="font-bold text-white">#{result.rankPosition}</span>
            </span>
            {typeof result.percentile === "number" &&
              result.percentile > 0 &&
              result.totalPlayers >= 10 && (
                <span className="text-[#38ef7d]">{t("betterThan", { n: result.percentile })}</span>
              )}
          </div>
        )}

        <div className="mt-5 grid w-full max-w-sm grid-cols-2 gap-2">
          {stats.map((s) => (
            <div
              key={s.label}
              className="flex items-center justify-between rounded-2xl border border-white/10 bg-white/5 px-4 py-3"
            >
              <span className="flex items-center gap-2 text-sm text-white/55">
                <s.icon size={16} style={{ color: s.color }} /> {s.label}
              </span>
              <span className="font-display text-base font-bold text-white">{s.value}</span>
            </div>
          ))}
        </div>

        <p className="mt-5 max-w-sm text-center text-sm italic text-white/60">« {dict.punchlines[rankKey]} »</p>
      </div>

      {/* Actions */}
      <div className="space-y-3">
        {owned.length > 0 && (
          <div>
            <p className="mb-2 text-xs uppercase tracking-widest text-white/40">{t("cardSkin")}</p>
            <div className="flex gap-2 overflow-x-auto pb-1" data-testid="skin-selector">
              <button
                onClick={() => setSkin("")}
                data-testid="skin-chip-default"
                className={`whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition-all ${
                  activeSkin === "" ? "border-[#f093fb] bg-[#f093fb] text-white" : "border-white/15 bg-white/5 text-white/60"
                }`}
              >
                {t("defaultSkin")}
              </button>
              {owned.map((id) =>
                SKIN_STYLES[id] ? (
                  <button
                    key={id}
                    onClick={() => setSkin(id)}
                    data-testid={`skin-chip-${id}`}
                    className="whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold text-white/70 transition-all"
                    style={
                      activeSkin === id
                        ? { background: SKIN_STYLES[id].c1, borderColor: SKIN_STYLES[id].c1, color: "#fff" }
                        : { borderColor: "rgba(255,255,255,0.15)" }
                    }
                  >
                    {SKIN_STYLES[id].name}
                  </button>
                ) : null
              )}
            </div>
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <motion.button
            whileTap={{ scale: 0.96 }}
            disabled={busy}
            onClick={handleShare}
            data-testid="share-button"
            className="flex items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-[#f093fb] to-[#f5576c] py-4 font-display font-bold text-white shadow-[0_8px_30px_-8px_rgba(240,147,251,0.6)] transition-opacity disabled:opacity-60"
          >
            <Share2 size={18} /> {t("share")}
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.96 }}
            disabled={busy}
            onClick={handleDownload}
            data-testid="download-button"
            className="flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 py-4 font-display font-bold text-white/85 transition-colors hover:bg-white/10 disabled:opacity-60"
          >
            <Download size={18} /> {t("download")}
          </motion.button>
        </div>
        <motion.button
          whileTap={{ scale: 0.96 }}
          onClick={onBonusReplay}
          data-testid="bonus-replay-button"
          className="flex w-full items-center justify-center gap-2 rounded-2xl border border-[#38ef7d]/40 bg-[#38ef7d]/10 py-3 text-sm font-semibold text-[#38ef7d] transition-colors hover:bg-[#38ef7d]/20"
        >
          <Gift size={16} /> {t("replayBonus")}
        </motion.button>
        <div className="grid grid-cols-2 gap-3">
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={onReplay}
            data-testid="replay-button"
            className="flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 py-4 font-semibold text-white/85 transition-colors hover:bg-white/10"
          >
            <RotateCcw size={18} /> {t("replay")}
          </motion.button>
          <motion.button
            whileTap={{ scale: 0.96 }}
            onClick={onLeaderboard}
            data-testid="result-leaderboard-button"
            className="flex items-center justify-center gap-2 rounded-2xl border border-white/15 bg-white/5 py-4 font-semibold text-white/85 transition-colors hover:bg-white/10"
          >
            <Trophy size={18} className="text-[#f093fb]" /> {t("leaderboard")}
          </motion.button>
        </div>
      </div>

      {/* Carte hors-écran pour capture haute résolution */}
      <div style={{ position: "fixed", left: -99999, top: 0, pointerEvents: "none" }} aria-hidden>
        <CareerCard
          ref={cardRef}
          result={result}
          domainLabel={domainLabel}
          skin={activeSkin || null}
          url={typeof window !== "undefined" ? window.location.origin : ""}
        />
      </div>
    </div>
  );
};
